import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, TextField, Typography, InputAdornment, Tooltip } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from '../../commons/snackBarContextProvider';

const COLOR_KEYS = [
  { key: 'background_color', label: 'Background Color' },
  { key: 'button_color',     label: 'Button Color' },
  { key: 'text_color',       label: 'Text Color' },
  { key: 'active_color',     label: 'Active Color' },
  { key: 'button_gradient',  label: 'Button Gradient' },
  { key: 'theme_seed_color', label: 'Theme Seed Color' },
  { key: 'card_gradient_1',  label: 'Card Gradient 1' },
  { key: 'card_gradient_2',  label: 'Card Gradient 2' },
];

const DEFAULTS = {
  background_color: '#ffffff',
  button_color:     '#1c3266',
  text_color:       '#1c3266',
  active_color:     '#e5a000',
  button_gradient:  '#e5a000',
  theme_seed_color: '#1c3266',
  card_gradient_1:  '#ffffff',
  card_gradient_2:  '#1c3266',
};

const isValidHex = (val) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val);

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': { padding: theme.spacing(2) },
  '& .MuiDialogActions-root': { padding: theme.spacing(1) },
}));

function BootstrapDialogTitle({ children, onClose, ...other }) {
  return (
    <DialogTitle sx={{ m: 0, p: 2, backgroundColor: 'button.menu' }} {...other}>
      {children}
      {onClose && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'icon.main' }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const ThemeDialogBox = ({ id, onClose }) => {
  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState({ ...DEFAULTS });
  const [errors, setErrors] = useState({});
  const { show } = useSnackbar();
  const token = JSON.parse(localStorage.getItem('adminData')).token;

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const fetchTheme = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_ADMIN}/theme/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data && res.data.data) {
        setColors({ ...DEFAULTS, ...res.data.data });
      }
    } catch (err) {
      console.error('Failed to fetch theme:', err);
    }
  };

  const handleChange = (key, value) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    // live validate
    if (value && !isValidHex(value)) {
      setErrors((prev) => ({ ...prev, [key]: 'Must be a valid hex code (e.g. #1c3266 or #fff)' }));
    } else {
      setErrors((prev) => { const e = { ...prev }; delete e[key]; return e; });
    }
  };

  // Sync native color-picker -> text field
  const handlePickerChange = (key, pickerValue) => {
    handleChange(key, pickerValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all
    const newErrors = {};
    COLOR_KEYS.forEach(({ key }) => {
      if (colors[key] && !isValidHex(colors[key])) {
        newErrors[key] = 'Invalid hex code';
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      show('Fix validation errors before saving', 'error');
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_ADMIN}/theme/${id}`,
        colors,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      show('Theme updated successfully');
      handleClose();
    } catch (err) {
      console.error('Theme update error:', err);
      show(err?.response?.data?.message || 'Failed to update theme', 'error');
    }
  };

  useEffect(() => {
    fetchTheme();
    setOpen(true);
    // eslint-disable-next-line
  }, [id]);

  return (
    <div>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="theme-dialog-title"
        open={open}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'background.paper',
            boxShadow: 'none',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '680px',
          },
        }}
      >
        <BootstrapDialogTitle id="theme-dialog-title" onClose={handleClose}>
          🎨 Mobile App Theme —&nbsp;
          <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{id}</span>
        </BootstrapDialogTitle>

        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: 'text.secondary', fontFamily: 'Poppins' }}
          >
            Enter valid hex codes (e.g.&nbsp;<code>#1c3266</code>&nbsp;or&nbsp;<code>#fff</code>).
            The colour picker on the right is a quick helper — the text field is the source of truth.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {COLOR_KEYS.map(({ key, label }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      id={`theme-field-${key}`}
                      fullWidth
                      label={label}
                      value={colors[key] || ''}
                      error={!!errors[key]}
                      helperText={errors[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      inputProps={{ maxLength: 7, spellCheck: false }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                backgroundColor: isValidHex(colors[key]) ? colors[key] : 'transparent',
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ fontFamily: 'Poppins' }}
                    />
                    <Tooltip title="Pick a colour" placement="top">
                      <Box
                        component="input"
                        id={`theme-picker-${key}`}
                        type="color"
                        value={isValidHex(colors[key]) ? colors[key] : '#000000'}
                        onChange={(e) => handlePickerChange(key, e.target.value)}
                        sx={{
                          mt: '8px',
                          width: '42px',
                          height: '42px',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          padding: '2px',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Button
                  fullWidth
                  type="submit"
                  sx={{
                    mt: 1,
                    backgroundColor: 'button.menu',
                    color: 'icon.main',
                    fontFamily: 'Poppins',
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    padding: '12px 0',
                    borderRadius: '10px',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: 'button.menuhover', boxShadow: 'none' },
                  }}
                >
                  Save Theme
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </BootstrapDialog>
    </div>
  );
};

ThemeDialogBox.propTypes = {
  id: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ThemeDialogBox;
